import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: 600,
      width: '100%',
    },
  },
  filterSection: {
    marginBottom: theme.spacing(2),
  },
  activeFilters: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  chipContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
}));

const statusOptions = [
  { value: 'CREATING', label: 'Criando' },
  { value: 'CREATED', label: 'Criado' },
  { value: 'PAYMENT_PENDING', label: 'Pagamento Pendente' },
  { value: 'PAYMENT_APPROVED', label: 'Pagamento Aprovado' },
  { value: 'WAITING_CONFIRMATION', label: 'Aguardando Confirmação' },
  { value: 'CONFIRMED', label: 'Confirmado' },
  { value: 'INVOICED', label: 'Faturado' },
  { value: 'IN_PREPARATION', label: 'Em Preparação' },
  { value: 'READY_TO_PICKUP', label: 'Pronto para Retirada' },
  { value: 'SHIPMENT', label: 'Em Transporte' },
  { value: 'DELIVERED', label: 'Entregue' },
  { value: 'CANCELED', label: 'Cancelado' },
  { value: 'RETURNED', label: 'Devolvido' },
];

const OrderFilters = ({ open, onClose, filters, onFiltersChange }) => {
  const classes = useStyles();

  // Local state for filters
  const [localFilters, setLocalFilters] = useState({
    status: '',
    platform: '',
    dateFrom: null,
    dateTo: null,
    ...filters,
  });

  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    if (open) {
      loadPlatforms();
      setLocalFilters({
        status: '',
        platform: '',
        dateFrom: null,
        dateTo: null,
        ...filters,
      });
    }
  }, [open, filters]);

  const loadPlatforms = async () => {
    try {
      // This would typically come from an API endpoint that returns available platforms
      // For now, we'll use common platforms
      setPlatforms([
        'iFood',
        'Americanas',
        'Shoptime',
        'Submarino',
        'B2W',
        'Mercado Livre',
        'Amazon',
        'Magazine Luiza',
        'Via Varejo',
        'Outros'
      ]);
    } catch (error) {
      console.error('Erro ao carregar plataformas:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    const cleanFilters = {};

    // Only include non-empty filters
    Object.entries(localFilters).forEach(([key, value]) => {
      if (value && value !== '') {
        if (value instanceof Date) {
          cleanFilters[key] = value.toISOString().split('T')[0];
        } else {
          cleanFilters[key] = value;
        }
      }
    });

    onFiltersChange(cleanFilters);
    onClose();
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      status: '',
      platform: '',
      dateFrom: null,
      dateTo: null,
    };
    setLocalFilters(emptyFilters);
    onFiltersChange({});
  };

  const getActiveFiltersCount = () => {
    return Object.values(localFilters).filter(value =>
      value && value !== '' && value !== null
    ).length;
  };

  const getActiveFiltersChips = () => {
    const chips = [];

    if (localFilters.status) {
      const statusLabel = statusOptions.find(opt => opt.value === localFilters.status)?.label;
      chips.push(
        <Chip
          key="status"
          label={`Status: ${statusLabel}`}
          onDelete={() => handleFilterChange('status', '')}
          size="small"
          color="primary"
        />
      );
    }

    if (localFilters.platform) {
      chips.push(
        <Chip
          key="platform"
          label={`Plataforma: ${localFilters.platform}`}
          onDelete={() => handleFilterChange('platform', '')}
          size="small"
          color="primary"
        />
      );
    }

    if (localFilters.dateFrom) {
      chips.push(
        <Chip
          key="dateFrom"
          label={`De: ${localFilters.dateFrom.toLocaleDateString('pt-BR')}`}
          onDelete={() => handleFilterChange('dateFrom', null)}
          size="small"
          color="primary"
        />
      );
    }

    if (localFilters.dateTo) {
      chips.push(
        <Chip
          key="dateTo"
          label={`Até: ${localFilters.dateTo.toLocaleDateString('pt-BR')}`}
          onDelete={() => handleFilterChange('dateTo', null)}
          size="small"
          color="primary"
        />
      );
    }

    return chips;
  };

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <DialogTitle>
        Filtros de Pedidos
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Status Filter */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth className={classes.filterSection}>
              <InputLabel>Status</InputLabel>
              <Select
                value={localFilters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {statusOptions.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Platform Filter */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth className={classes.filterSection}>
              <InputLabel>Plataforma</InputLabel>
              <Select
                value={localFilters.platform}
                onChange={(e) => handleFilterChange('platform', e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {platforms.map((platform) => (
                  <MenuItem key={platform} value={platform}>
                    {platform}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Date From Filter */}
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Data Inicial"
              value={localFilters.dateFrom}
              onChange={(date) => handleFilterChange('dateFrom', date)}
              format="dd/MM/yyyy"
              fullWidth
              inputVariant="outlined"
              clearable
              className={classes.filterSection}
            />
          </Grid>

          {/* Date To Filter */}
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Data Final"
              value={localFilters.dateTo}
              onChange={(date) => handleFilterChange('dateTo', date)}
              format="dd/MM/yyyy"
              fullWidth
              inputVariant="outlined"
              clearable
              className={classes.filterSection}
            />
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {getActiveFiltersCount() > 0 && (
          <Box className={classes.activeFilters}>
            <Typography variant="subtitle2" gutterBottom>
              Filtros Ativos ({getActiveFiltersCount()}):
            </Typography>
            <div className={classes.chipContainer}>
              {getActiveFiltersChips()}
            </div>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button onClick={handleClearFilters} color="secondary">
          Limpar Filtros
        </Button>
        <Button
          onClick={handleApplyFilters}
          color="primary"
          variant="contained"
        >
          Aplicar Filtros
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderFilters;